import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Clock, PawPrint, User, MessageCircle, Phone, ChevronRight, CalendarCheck } from 'lucide-react';
import { toast } from 'sonner';
import { BUSINESS_NAME, CURRENCY_SYMBOL } from '../../config/brand';

function buildConfirmationMsg(b) {
  return `Hi ${b.client_name}! Your ${BUSINESS_NAME} grooming appointment is confirmed.\n\nPet: ${b.pet_name} (${b.pet_breed})\nService: ${b.service_names?.join(', ')}\nDate: ${b.date}\nTime: ${b.time_slot}\nGroomer: ${b.staff_name}\nTotal: ${CURRENCY_SYMBOL}${b.total_price}\n\nSee you soon! - ${BUSINESS_NAME}`;
}

export default function AdminUpcomingArrivals() {
  const [arrivals, setArrivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [sandboxInfo, setSandboxInfo] = useState(null);
  const [showSetup, setShowSetup] = useState(false);

  useEffect(() => {
    api.get('/admin/upcoming-arrivals')
      .then(r => { setArrivals(r.data); setLoading(false); })
      .catch(() => setLoading(false));
    api.get('/admin/whatsapp/status')
      .then(r => setSandboxInfo(r.data))
      .catch(() => {});
  }, []);

  const today = new Date().toISOString().split('T')[0];
  const todayArrivals = arrivals.filter(a => a.date === today);
  const upcomingArrivals = arrivals.filter(a => a.date > today);

  const handleTwilioSend = async (booking) => {
    if (!booking.client_phone) {
      toast.error('Customer phone number not available');
      return;
    }
    setSending(prev => ({ ...prev, [booking.appointment_id]: true }));
    try {
      const msg = buildConfirmationMsg(booking);
      await api.post('/admin/whatsapp/send', { phone: booking.client_phone, message: msg });
      toast.success(`WhatsApp sent to ${booking.client_name}`);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Send failed';
      toast.error(`WhatsApp failed: ${detail}`);
    }
    setSending(prev => ({ ...prev, [booking.appointment_id]: false }));
  };

  const handleWaLink = (booking) => {
    const phone = (booking.client_phone || '').replace(/[^0-9]/g, '');
    const waPhone = phone.startsWith('91') ? phone : `91${phone}`;
    const msg = buildConfirmationMsg(booking);
    window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/bookings/${id}/status`, { status });
      setArrivals(prev => prev.map(a => a.appointment_id === id ? { ...a, status } : a));
      toast.success(`Status updated to ${status}`);
    } catch {
      toast.error('Failed to update');
    }
  };

  const statusColor = (s) => {
    const map = {
      pending: 'bg-amber-100 text-amber-800', confirmed: 'bg-blue-100 text-blue-800',
      checked_in: 'bg-indigo-100 text-indigo-800',
    };
    return map[s] || 'bg-gray-100 text-gray-800';
  };

  const ArrivalCard = ({ booking }) => (
    <Card className="rounded-2xl border-[#E8DDD4] bg-white hover:shadow-md transition-shadow" data-testid={`arrival-card-${booking.appointment_id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-[#F5EDE6] rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-[#6A3E28]" />
            </div>
            <div>
              <p className="text-lg font-bold text-[#3D2418]">{booking.time_slot}</p>
              <p className="text-xs text-[#A09387]">{booking.date}</p>
            </div>
          </div>
          <span className={`text-xs font-medium px-3 py-1 rounded-full ${statusColor(booking.status)}`}>
            {booking.status.replace('_', ' ')}
          </span>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <div className="flex items-center gap-2.5">
            <User className="w-4 h-4 text-[#A09387]" />
            <div>
              <p className="text-sm font-medium text-[#3D2418]">{booking.client_name}</p>
              {booking.client_phone && <p className="text-xs text-[#A09387]">{booking.client_phone}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <PawPrint className="w-4 h-4 text-[#A09387]" />
            <div>
              <p className="text-sm font-medium text-[#3D2418]">{booking.pet_name}</p>
              <p className="text-xs text-[#A09387]">{booking.pet_breed}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {booking.service_names?.map((name, i) => (
            <span key={i} className="text-xs bg-[#F5EDE6] text-[#6A3E28] px-2.5 py-1 rounded-lg font-medium">{name}</span>
          ))}
        </div>

        <div className="flex items-center justify-between text-sm text-[#7A6B5E] mb-4">
          <span>Groomer: <strong className="text-[#3D2418]">{booking.staff_name}</strong></span>
          <span className="font-bold text-[#A96B49]">{CURRENCY_SYMBOL}{booking.total_price}</span>
        </div>

        <div className="flex items-center gap-2 pt-3 border-t border-[#E8DDD4]">
          <Select value={booking.status} onValueChange={(val) => updateStatus(booking.appointment_id, val)}>
            <SelectTrigger className="w-32 h-8 text-xs rounded-lg" data-testid={`arrival-status-${booking.appointment_id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="checked_in">Checked In</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>

          <Button
            size="sm"
            className="h-8 rounded-lg bg-[#25D366] hover:bg-[#1DA851] text-white text-xs gap-1.5"
            onClick={() => handleTwilioSend(booking)}
            disabled={sending[booking.appointment_id] || !booking.client_phone}
            data-testid={`twilio-send-${booking.appointment_id}`}
          >
            <MessageCircle className="w-3.5 h-3.5" />
            {sending[booking.appointment_id] ? 'Sending...' : 'Send WhatsApp'}
          </Button>

          {booking.client_phone && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 rounded-lg border-[#25D366] text-[#25D366] hover:bg-[#25D366]/5 text-xs gap-1.5"
              onClick={() => handleWaLink(booking)}
              data-testid={`wa-link-${booking.appointment_id}`}
            >
              <Phone className="w-3.5 h-3.5" />
              Open Chat
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#6A3E28] border-t-transparent" /></div>;

  return (
    <div className="space-y-6" data-testid="upcoming-arrivals-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#3D2418]" style={{ fontFamily: 'Manrope' }}>Upcoming Arrivals</h1>
        <p className="text-sm text-[#7A6B5E] mt-1">{arrivals.length} upcoming appointment{arrivals.length !== 1 ? 's' : ''}</p>
      </div>

      {/* WhatsApp Sandbox Setup Info */}
      {sandboxInfo && (
        <Card className="rounded-2xl border-[#25D366]/30 bg-[#25D366]/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-[#25D366]" />
                <div>
                  <p className="text-sm font-semibold text-[#3D2418]">WhatsApp via Twilio Sandbox</p>
                  <p className="text-xs text-[#7A6B5E]">Customers must join sandbox before receiving messages</p>
                </div>
              </div>
              <Button variant="outline" size="sm" className="rounded-lg text-xs border-[#25D366] text-[#25D366]" onClick={() => setShowSetup(!showSetup)} data-testid="toggle-wa-setup">
                {showSetup ? 'Hide Setup' : 'Setup Guide'}
              </Button>
            </div>
            {showSetup && (
              <div className="mt-3 p-3 bg-white rounded-xl text-sm text-[#5A4E44] space-y-2 border border-[#E8DDD4]">
                <p className="font-semibold text-[#3D2418]">How to enable WhatsApp sending:</p>
                <ol className="list-decimal pl-5 space-y-1.5">
                  <li>Go to <a href="https://console.twilio.com/us1/develop/sms/try-it-out/whatsapp-learn" target="_blank" rel="noopener noreferrer" className="text-[#6A3E28] underline font-medium">Twilio Console → Messaging → Try it out → WhatsApp</a></li>
                  <li>Find your sandbox keyword (e.g. <code className="bg-[#F5EDE6] px-1.5 py-0.5 rounded text-xs">join example-word</code>)</li>
                  <li>Have each customer send that keyword to <strong>+14155238886</strong> on WhatsApp</li>
                  <li>After joining, they can receive booking confirmations from {BUSINESS_NAME}</li>
                </ol>
                <p className="text-xs text-[#A09387] pt-1">Sandbox number: {sandboxInfo.sandbox_number} | Status: {sandboxInfo.configured ? 'Configured' : 'Not configured'}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {arrivals.length === 0 ? (
        <Card className="rounded-2xl border-[#E8DDD4] border-dashed bg-white">
          <CardContent className="p-10 text-center">
            <CalendarCheck className="w-12 h-12 text-[#C48C68] mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-sm text-[#7A6B5E]">No upcoming arrivals scheduled</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {todayArrivals.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-[#3D2418] mb-3 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#4A7C59] animate-pulse" />
                Today ({todayArrivals.length})
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {todayArrivals.map(b => <ArrivalCard key={b.appointment_id} booking={b} />)}
              </div>
            </div>
          )}

          {upcomingArrivals.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-[#3D2418] mb-3">Upcoming ({upcomingArrivals.length})</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {upcomingArrivals.map(b => <ArrivalCard key={b.appointment_id} booking={b} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
